import { FC, useEffect, useState, useCallback, useMemo } from 'react';
import { Wrapper, FormContent, Table, Empty, EllipsisSpan } from '@maxtropy/components';
import styles from './index.module.scss';
import { Form, Row, Col, Space, Button, Tag, TableColumnsType } from 'antd';
import ShowInput from '@/shared/components/ShowInput';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { AlarmLevelColorDisplay, AlarmLevelDisplay, AlarmChannel } from '@/shared/types';
import {
  apiV2RuleAlarmLogDetailsPost,
  V2RuleAlarmLogDetailsPostResponse,
  apiV2RuleAlarmLogRuleRelatedPropertyValueListPost,
  V2RuleAlarmLogRuleRelatedPropertyValueListPostResponse,
} from '@maxtropy/tody-mgmt-apis-v2';
import EnergyAssetInfoModal from '../List/EnergyAssetInfoModal';
import { deviceAssetsResponse } from '../List';
import { isEmpty, isNil } from 'lodash-es';
import dayjs from 'dayjs';

const dateFormat = 'YYYY/MM/DD HH:mm:ss';

const formLayout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 18 },
};

const routes = [{ name: 'Iot配置' }, { name: '报警推送' }, { name: '设备报警记录' }, { name: '记录详情' }];

type RelatedPropertyTableData = {
  key?: React.Key;
  dataPropertyNameWithUnit?: string;
  alarmValue?: number;
  recoverValue?: number;
  realtimeValue?: number;
};

export interface AlarmLogDetailResponse extends V2RuleAlarmLogDetailsPostResponse {
  durationTime?: string;
}

const relatedPropertyTableColumns: TableColumnsType<RelatedPropertyTableData> = [
  {
    title: '数据属性',
    dataIndex: 'dataPropertyNameWithUnit',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '触发时',
    dataIndex: 'alarmValue',
    ellipsis: { showTitle: true },
    render: (v: number) => (isNil(v) ? '--' : <EllipsisSpan value={v} />),
  },
  {
    title: '恢复时',
    dataIndex: 'recoverValue',
    ellipsis: { showTitle: true },
    render: (v: number) => (isNil(v) ? '--' : <EllipsisSpan value={v} />),
  },
  {
    title: '实时值',
    dataIndex: 'realtimeValue',
    ellipsis: { showTitle: true },
    render: (v: number) => (isNil(v) ? '--' : <EllipsisSpan value={v} />),
  },
];

const AlarmRecordDetail: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<AlarmLogDetailResponse>();
  const [open, setOpen] = useState<boolean>(false);
  const [energyAssetInfo, setEnergyAssetInfo] = useState<deviceAssetsResponse>([]);
  const [relatedProperties, setRelatedProperties] = useState<
    V2RuleAlarmLogRuleRelatedPropertyValueListPostResponse['list']
  >([]);

  useEffect(() => {
    if (id) {
      apiV2RuleAlarmLogDetailsPost({ id }).then(res => {
        let durationText = '';

        if (res.recoveryTime && res.alarmTime) {
          const diff = dayjs.duration(dayjs(res.recoveryTime).diff(dayjs(res.alarmTime)));
          if (diff.days() > 0) durationText += `${diff.days()}天`;
          if (diff.hours() > 0) durationText += `${diff.hours()}时`;
          if (diff.minutes() > 0) durationText += `${diff.minutes()}分`;
        }
        setData({
          ...res,
          durationTime: durationText,
        });
      });
    }
  }, [id]);

  const relatedPropertyTableData: RelatedPropertyTableData[] = useMemo(() => {
    return (relatedProperties ?? []).map(v => ({
      key: v.dataPropertyId,
      dataPropertyNameWithUnit: v.generalName
        ? `${v.dataPropertyName}（${v.generalName}）`
        : `${v.dataPropertyName}（--）`,
      alarmValue: v.alarmValue,
      recoverValue: v.recoverValue,
      realtimeValue: v.realtimeValue,
    }));
  }, [relatedProperties]);

  const fetchRelatedProperties = useCallback(() => {
    if (id) {
      apiV2RuleAlarmLogRuleRelatedPropertyValueListPost({ id }).then(res => {
        setRelatedProperties(res.list ?? []);
      });
    }
  }, [id]);

  const showEnergyAssetInfo = (val: deviceAssetsResponse) => {
    setOpen(true);
    setEnergyAssetInfo(val);
  };
  return (
    <Wrapper routes={routes} className={styles.wrapper}>
      <Form layout="vertical" {...formLayout}>
        <FormContent>
          <Row>
            <Col span={8} className={styles.col}>
              <Form.Item label="所属租户">
                <ShowInput value={data?.tenantName} />
              </Form.Item>
            </Col>
            <Col span={8} className={styles.col}>
              <Form.Item label="设备编号">
                <Space>
                  <ShowInput value={data?.deviceCode} />
                  <Button type="link">
                    <Link to={`/device/manage/device/${data?.deviceId}/detail`}>查看</Link>
                  </Button>
                </Space>
              </Form.Item>
            </Col>
            <Col span={8} className={styles.col}>
              <Form.Item label="设备名称">
                <ShowInput value={data?.deviceName} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="能源资产信息">
                {!data?.deviceAssets || data?.deviceAssets.length === 0 ? (
                  '暂无'
                ) : (
                  <Button style={{ paddingLeft: 0 }} type="link" onClick={() => showEnergyAssetInfo(data.deviceAssets)}>
                    点击查看
                  </Button>
                )}
              </Form.Item>
            </Col>
            <Col span={8} className={styles.col}>
              <Form.Item label="报警等级">
                <ShowInput
                  value={
                    <Tag color={AlarmLevelColorDisplay[data?.alarmLevel as keyof typeof AlarmLevelColorDisplay]}>
                      {AlarmLevelDisplay[data?.alarmLevel! as keyof typeof AlarmLevelDisplay]}
                    </Tag>
                  }
                />
              </Form.Item>
            </Col>
            <Col span={8} className={styles.col}>
              <Form.Item label="报警信息">
                <ShowInput value={data?.alarmName} />
              </Form.Item>
            </Col>
            <Col span={8} className={styles.col}>
              <Form.Item label="报警规则">
                <Space>
                  <ShowInput value={data?.ruleName} />
                  <Button type="link">
                    <Link to={`/device/rule/list/detail/${data?.ruleId}`}>查看</Link>
                  </Button>
                </Space>
              </Form.Item>
            </Col>
            <Col span={8} className={styles.col}>
              <Form.Item label="规则编号">
                <ShowInput value={data?.ruleCode} />
              </Form.Item>
            </Col>
            <Col span={8} className={styles.col}>
              <Form.Item label="报警时间">
                <ShowInput value={dayjs(data?.alarmTime).format(dateFormat)} />
              </Form.Item>
            </Col>
            <Col span={8} className={styles.col}>
              <Form.Item label="恢复时间">
                <ShowInput value={data?.recoveryTime ? dayjs(data?.recoveryTime).format(dateFormat) : '未恢复'} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="持续时间">
                <ShowInput value={data?.durationTime ?? '--'} />
              </Form.Item>
            </Col>
            <Col span={8} className={styles.col}>
              <Form.Item name="readTime" label="已读时间">
                <ShowInput />
              </Form.Item>
            </Col>
             <Col span={8}>
              <Form.Item name="alarmFormula" label="报警公式">
                <Space size={60}>
                  <ShowInput value={data?.alarmFormula ?? '--'} />
                </Space>
              </Form.Item>
            </Col>
            {/* 若为网关离线报警或者渠道是朗新, 不显示关联属性 */}
            {data?.edgeGatewayNo || data?.channel === AlarmChannel.LONGSHINE ? null : (
              <Col span={20}>
                <Form.Item
                  label="关联属性"
                  // tooltip={{
                  //   title: '根据时间，前后3分钟范围内无数据则不显示。实时值每分钟更新。',
                  //   icon: <InfoCircleOutlined />,
                  // }}
                >
                  {isEmpty(relatedProperties) ? (
                    <Empty description="报警规则未配置关联属性" />
                  ) : (
                    <>
                      <Table
                        bordered
                        // 如果为 0 说明报警已恢复, 无需展示实时值
                        columns={
                          data?.state === 'RECOVERY'
                            ? relatedPropertyTableColumns.slice(0, relatedPropertyTableColumns.length - 1)
                            : relatedPropertyTableColumns
                        }
                        dataSource={relatedPropertyTableData}
                        pagination={false}
                      />
                    </>
                  )}
                </Form.Item>
              </Col>
            )}
          </Row>
        </FormContent>
        <Space className="sticky-footer">
          <Button
            className={styles.button}
            onClick={() => {
              navigate(`/device/alarm/record`);
            }}
          >
            返回
          </Button>
        </Space>
      </Form>
      {open && <EnergyAssetInfoModal setOpen={setOpen} energyAssetInfo={energyAssetInfo} />}
    </Wrapper>
  );
};

export default AlarmRecordDetail;
